import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Plus, ChevronLeft, ChevronRight, CalendarDays, User, Car, Trash2, X, Clock, Wrench, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Pondelok", "Utorok", "Streda", "Štvrtok", "Piatok", "Sobota", "Nedeľa"];
const pad = (n) => String(n).padStart(2, "0");
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (d) => { const x = new Date(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const todayStr = fmt(new Date());

function EntityPicker({ label, icon: Icon, items, value, onChange, getLabel, placeholder }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const selected = items.find((i) => i.id === value);
  const filtered = (q ? items.filter((i) => getLabel(i).toLowerCase().includes(q.toLowerCase())) : items).slice(0, 8);
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {label}</Label>
      {selected ? (
        <div className="flex items-center justify-between rounded-md border border-border bg-background/50 px-3 py-2 text-sm">
          <span className="truncate">{getLabel(selected)}</span>
          <button type="button" onClick={() => onChange(null)} className="text-muted-foreground hover:text-foreground" data-testid={`clear-${label}`}><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <div className="relative">
          <Input value={q} placeholder={placeholder} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} data-testid={`picker-${label}`} />
          {open && (
            <div className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-md border border-border bg-popover shadow-lg">
              {filtered.map((i) => (
                <button key={i.id} type="button" onMouseDown={(e) => { e.preventDefault(); onChange(i.id); setOpen(false); setQ(""); }}
                  className="block w-full border-b border-border/50 px-3 py-2 text-left text-sm transition-colors last:border-0 hover:bg-accent">{getLabel(i)}</button>
              ))}
              {!filtered.length && <div className="px-3 py-2 text-sm text-muted-foreground">Žiadne výsledky</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const empty = (dateStr) => ({ id: null, dateStr: dateStr || todayStr, timeStr: "09:00", note: "", customer_id: null, vehicle_id: null, job_id: null });

export default function Planner() {
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [appts, setAppts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const from = fmt(weekStart), to = fmt(addDays(weekStart, 6));
    try {
      const { data } = await api.get(`/appointments?date_from=${from}&date_to=${to}`);
      setAppts(data);
    } catch { toast.error("Nepodarilo sa načítať termíny"); }
  }, [weekStart]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const custName = (id) => customers.find((c) => c.id === id)?.name || "";
  const vehLabel = (id) => { const v = vehicles.find((x) => x.id === id); return v ? `${v.spz || ""} ${v.brand || ""} ${v.model || ""}`.trim() : ""; };
  const vehForCust = (cid) => (cid ? vehicles.filter((v) => v.customer_id === cid) : vehicles);

  const openNew = (dateStr) => setDlg(empty(dateStr));
  const openEdit = (a) => { const [d, t] = (a.start || "").split("T"); setDlg({ id: a.id, dateStr: d || todayStr, timeStr: (t || "09:00").slice(0, 5), note: a.note || "", customer_id: a.customer_id || null, vehicle_id: a.vehicle_id || null, job_id: a.job_id || null }); };

  const save = async () => {
    if (!dlg.note.trim() && !dlg.customer_id) { toast.error("Napíšte poznámku alebo priraďte zákazníka"); return; }
    setSaving(true);
    const payload = { start: `${dlg.dateStr}T${dlg.timeStr}:00`, note: dlg.note, customer_id: dlg.customer_id, vehicle_id: dlg.vehicle_id };
    try {
      if (dlg.id) await api.put(`/appointments/${dlg.id}`, payload);
      else await api.post("/appointments", payload);
      toast.success("Termín uložený");
      setDlg(null); load();
    } catch { toast.error("Nepodarilo sa uložiť"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/appointments/${dlg.id}`); toast.success("Termín zmazaný"); setDlg(null); load(); }
    catch { toast.error("Nepodarilo sa zmazať"); }
  };

  const convertToJob = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/appointments/${dlg.id}/convert`, {});
      toast.success("Zákazka vytvorená");
      setDlg(null);
      navigate(`/zakazky/${data.id}/upravit`);
    } catch { toast.error("Nepodarilo sa vytvoriť zákazku"); }
    finally { setSaving(false); }
  };

  const byDay = (dateStr) => appts.filter((a) => (a.start || "").slice(0, 10) === dateStr).sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  const rangeLabel = `${weekStart.getDate()}.${weekStart.getMonth() + 1}. – ${addDays(weekStart, 6).getDate()}.${addDays(weekStart, 6).getMonth() + 1}.${addDays(weekStart, 6).getFullYear()}`;

  return (
    <div data-testid="planner-page">
      <PageHeader title="Plánovač" subtitle="Naplánujte si termíny opráv. Zákazku z termínu vytvoríte tlačidlom, keď sa oprava začne."
        action={<Button className="min-h-[48px] gap-2" onClick={() => openNew(todayStr)} data-testid="planner-new"><Plus className="h-5 w-5" /> Nový termín</Button>} />

      <div className="mb-4 flex items-center justify-between gap-2">
        <Button variant="outline" size="sm" className="gap-1" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="planner-prev-week"><ChevronLeft className="h-4 w-4" /> Predch.</Button>
        <div className="flex items-center gap-3">
          <span className="font-heading text-lg font-bold" data-testid="planner-range">{rangeLabel}</span>
          <Button variant="secondary" size="sm" onClick={() => setWeekStart(mondayOf(new Date()))} data-testid="planner-today">Dnes</Button>
        </div>
        <Button variant="outline" size="sm" className="gap-1" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="planner-next-week">Ďalší <ChevronRight className="h-4 w-4" /></Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-7">
        {DAYS.map((dayName, idx) => {
          const d = addDays(weekStart, idx);
          const ds = fmt(d);
          const isToday = ds === todayStr;
          const list = byDay(ds);
          return (
            <div key={idx} className={`flex flex-col rounded-xl border p-2 ${isToday ? "border-primary/60 bg-primary/5" : "border-border bg-card"}`} data-testid={`planner-day-${idx}`}>
              <div className="mb-2 flex items-center justify-between px-1">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{dayName.slice(0, 3)}</div>
                  <div className={`font-heading text-lg font-bold ${isToday ? "text-primary" : ""}`}>{d.getDate()}.{d.getMonth() + 1}.</div>
                </div>
                <button onClick={() => openNew(ds)} className="grid h-7 w-7 place-items-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-accent hover:text-foreground" data-testid={`planner-add-${ds}`} aria-label="Pridať termín"><Plus className="h-4 w-4" /></button>
              </div>
              <div className="space-y-1.5">
                {list.map((a) => (
                  <button key={a.id} onClick={() => openEdit(a)} data-testid={`appt-${a.id}`}
                    className="w-full rounded-lg border border-border bg-background/60 p-2 text-left transition-colors hover:border-primary/50">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-primary"><Clock className="h-3 w-3" />{(a.start || "").slice(11, 16)}{a.job_id && <span className="ml-auto inline-flex items-center gap-0.5 text-emerald-400"><Wrench className="h-3 w-3" /></span>}</div>
                    {a.note && <div className="mt-0.5 line-clamp-2 text-sm">{a.note}</div>}
                    {a.customer_id && <div className="mt-1 flex items-center gap-1 truncate text-xs text-muted-foreground"><User className="h-3 w-3 shrink-0" />{custName(a.customer_id)}</div>}
                    {a.vehicle_id && <div className="flex items-center gap-1 truncate text-xs text-muted-foreground"><Car className="h-3 w-3 shrink-0" />{vehLabel(a.vehicle_id)}</div>}
                  </button>
                ))}
                {!list.length && <div className="px-1 py-3 text-center text-xs text-muted-foreground/60">—</div>}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => { if (!o) setDlg(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="appointment-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">{dlg?.id ? "Upraviť termín" : "Nový termín"}</DialogTitle><DialogDescription>Dátum, čas a poznámka. Zákazník a vozidlo sú nepovinné.</DialogDescription></DialogHeader>
          {dlg && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Dátum</Label><Input type="date" className="h-12 min-w-0 appearance-none px-3 [-webkit-appearance:none] [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-date-and-time-value]:text-left" value={dlg.dateStr} onChange={(e) => setDlg({ ...dlg, dateStr: e.target.value })} data-testid="appt-date" /></div>
                <div className="space-y-1.5"><Label>Čas</Label><Input type="time" className="h-12 min-w-0 appearance-none px-3 [-webkit-appearance:none] [&::-webkit-calendar-picker-indicator]:opacity-60 [&::-webkit-date-and-time-value]:text-left" value={dlg.timeStr} onChange={(e) => setDlg({ ...dlg, timeStr: e.target.value })} data-testid="appt-time" /></div>
              </div>
              <div className="space-y-1.5"><Label>Poznámka</Label><Textarea rows={2} placeholder="napr. Výmena oleja, telefón 0905..." value={dlg.note} onChange={(e) => setDlg({ ...dlg, note: e.target.value })} data-testid="appt-note" /></div>
              <EntityPicker label="Zákazník" icon={User} items={customers} value={dlg.customer_id} getLabel={(c) => c.name} placeholder="Vyhľadať zákazníka (nepovinné)" onChange={(id) => setDlg({ ...dlg, customer_id: id, vehicle_id: null })} />
              <EntityPicker label="Vozidlo" icon={Car} items={vehForCust(dlg.customer_id)} value={dlg.vehicle_id} getLabel={(v) => `${v.spz || ""} ${v.brand || ""} ${v.model || ""}`.trim()} placeholder="Vyhľadať vozidlo (nepovinné)" onChange={(id) => setDlg({ ...dlg, vehicle_id: id })} />
              {dlg.id && <div className="flex items-start gap-2 rounded-md border border-sky-500/30 bg-sky-500/10 p-2 text-xs text-sky-400"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> Z termínu vytvoríte zákazku tlačidlom „Vytvoriť zákazku". Termín sám o sebe nevytvára zákazku.</div>}
            </div>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            <div className="flex gap-2">
              {dlg?.id && <Button variant="ghost" className="gap-1.5 text-destructive hover:text-destructive" onClick={remove} data-testid="appt-delete"><Trash2 className="h-4 w-4" /> Zmazať</Button>}
              {dlg?.id && <Button variant="outline" className="gap-1.5" onClick={convertToJob} disabled={saving} data-testid="appt-convert"><Wrench className="h-4 w-4" /> Vytvoriť zákazku</Button>}
            </div>
            <Button className="min-h-[44px]" onClick={save} disabled={saving} data-testid="appt-save">Uložiť termín</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
